import type {
  LeapBotActionCard,
  LeapBotActionData,
  LeapBotAgentEvent,
  LeapBotMessage,
  LeapBotSource,
  LeapBotTicket,
} from '../../core/interfaces/leapbot.interface';

/** Same pattern LeapBot validates for `threadId` (any UUID version). */
const THREAD_ID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/** Marker appended to an answer the user stopped. */
export const ABORTED_SUFFIX = ' [Generación abortada]';

/** Title of a conversation that has no user message yet. */
export const EMPTY_CONVERSATION_TITLE = 'Nueva conversación';

const TITLE_MAX_LENGTH = 60;

/**
 * Whether a value is a UUID that LeapBot accepts as `threadId`.
 *
 * @param value - Candidate thread id.
 * @example
 * isValidThreadId('550e8400-e29b-41d4-a716-446655440000'); // true
 * isValidThreadId('abc'); // false
 */
export function isValidThreadId(value: string): boolean {
  return THREAD_ID_PATTERN.test(value);
}

/**
 * Returns the URL only when it is an absolute `https://` URL; any other scheme (`http:`,
 * `javascript:`, `data:`) or a relative value yields `null`.
 *
 * @param url - Untrusted URL coming from LeapBot.
 * @example
 * toHttpsUrl('https://auth.atlassian.com/authorize?x=1'); // 'https://auth.atlassian.com/authorize?x=1'
 * toHttpsUrl('javascript:alert(1)'); // null
 */
export function toHttpsUrl(url: string | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' ? parsed.href : null;
  } catch {
    return null;
  }
}

/**
 * Builds the conversation title: the first 60 characters of the first user message, the name of
 * its first file when it only had files, or `Nueva conversación` while there is none.
 *
 * @param messages - Messages of the conversation, in order.
 */
export function buildConversationTitle(messages: readonly LeapBotMessage[]): string {
  const first = messages.find((m) => m.role === 'user' && m.content.trim().length > 0);
  if (first) return first.content.trim().slice(0, TITLE_MAX_LENGTH);
  // A conversation that started with files only is titled after its first file.
  const withFiles = messages.find((m) => m.role === 'user' && (m.attachments?.length ?? 0) > 0);
  return withFiles?.attachments?.[0] ? withFiles.attachments[0].name.slice(0, TITLE_MAX_LENGTH) : EMPTY_CONVERSATION_TITLE;
}

const KNOWN_EVENT_TYPES = new Set(['TokenStream', 'ToolStart', 'ToolEnd', 'Error', 'ActionRequired', 'partial_stream_error']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Narrows a raw `agent_event` payload to a known event, or `null` for anything else
 * (unknown types, malformed payloads). Never throws.
 *
 * @param payload - Raw value received from the socket.
 */
export function parseAgentEvent(payload: unknown): LeapBotAgentEvent | null {
  if (!isRecord(payload) || typeof payload['type'] !== 'string') return null;
  if (!KNOWN_EVENT_TYPES.has(payload['type'])) return null;
  const base = {
    threadId: typeof payload['threadId'] === 'string' ? payload['threadId'] : '',
    timestamp: typeof payload['timestamp'] === 'number' ? payload['timestamp'] : 0,
  };
  switch (payload['type']) {
    case 'TokenStream':
      return typeof payload['content'] === 'string'
        ? { ...base, type: 'TokenStream', content: payload['content'] }
        : null;
    case 'ToolStart':
      return typeof payload['toolName'] === 'string'
        ? {
            ...base,
            type: 'ToolStart',
            toolName: payload['toolName'],
            input: isRecord(payload['input']) ? (payload['input'] as Record<string, unknown>) : undefined, // tool arguments, read defensively
          }
        : null;
    case 'ToolEnd':
      return typeof payload['toolName'] === 'string'
        ? { ...base, type: 'ToolEnd', toolName: payload['toolName'], output: payload['output'] }
        : null;
    case 'Error':
      return {
        ...base,
        type: 'Error',
        message: typeof payload['message'] === 'string' ? payload['message'] : '',
        code: typeof payload['code'] === 'string' ? payload['code'] : undefined,
      };
    case 'ActionRequired':
      return {
        ...base,
        type: 'ActionRequired',
        data: isRecord(payload['data']) ? (payload['data'] as LeapBotActionData) : {}, // LeapBot payload, fields read defensively below
      };
    case 'partial_stream_error':
      return {
        ...base,
        type: 'partial_stream_error',
        message: typeof payload['message'] === 'string' ? payload['message'] : '',
        tokenChunksEmitted: typeof payload['tokenChunksEmitted'] === 'number' ? payload['tokenChunksEmitted'] : 0,
      };
    default:
      return null;
  }
}

/**
 * Appends a streamed chunk to the answer in progress, or starts a new assistant message when
 * the last message is not an open assistant answer.
 *
 * @param messages - Current messages.
 * @param chunk - Text received from `TokenStream`.
 * @param id - Id to use if a new message must be created.
 * @param now - ISO time to use if a new message must be created.
 */
export function appendToken(
  messages: readonly LeapBotMessage[],
  chunk: string,
  id: string,
  now: string,
): LeapBotMessage[] {
  const last = messages[messages.length - 1];
  if (last && last.role === 'assistant' && !last.isComplete) {
    return [...messages.slice(0, -1), { ...last, content: last.content + chunk }];
  }
  return [...messages, { id, role: 'assistant', content: chunk, timestamp: now, isComplete: false }];
}

/** Marks the open assistant answer, if any, as complete. */
export function finalizeStreaming(messages: readonly LeapBotMessage[]): LeapBotMessage[] {
  const last = messages[messages.length - 1];
  if (last && last.role === 'assistant' && !last.isComplete) {
    return [...messages.slice(0, -1), { ...last, isComplete: true }];
  }
  return [...messages];
}

/**
 * Marks the last assistant answer as complete but cut off, so the UI can flag it. Does nothing
 * when the conversation has no assistant message yet.
 */
export function markTruncated(messages: readonly LeapBotMessage[]): LeapBotMessage[] {
  const index = messages.map((m) => m.role).lastIndexOf('assistant');
  if (index === -1) return [...messages];
  return messages.map((m, i) => (i === index ? { ...m, isComplete: true, isTruncated: true } : m));
}

/**
 * Completes the open assistant answer marking it as stopped by the user. An open answer with
 * no text is removed so no empty message stays in the conversation.
 */
export function abortStreaming(messages: readonly LeapBotMessage[]): LeapBotMessage[] {
  const last = messages[messages.length - 1];
  if (!last || last.role !== 'assistant' || last.isComplete) return [...messages];
  if (last.content.length === 0) return messages.slice(0, -1);
  return [...messages.slice(0, -1), { ...last, isComplete: true, content: last.content + ABORTED_SUFFIX }];
}

const TICKET_TOOL_NAME = 'create_jira_ticket';
const KNOWLEDGE_TOOL_NAME = 'search_knowledge_base';
const MAX_SOURCES = 3;

function parseOutput(output: unknown): unknown {
  if (typeof output !== 'string') return output;
  try {
    return JSON.parse(output);
  } catch {
    return null;
  }
}

function firstString(source: unknown, keys: readonly string[]): string | undefined {
  if (!isRecord(source)) return undefined;
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return undefined;
}

/**
 * Builds the ticket card after a successful `create_jira_ticket`, or `null` when the tool is
 * another one, did not succeed, or returned no ticket key.
 *
 * @param toolName - Tool that ended.
 * @param output - Raw output of the tool (JSON string or object); its shape is decided by LeapBot.
 * @param input - Arguments the agent passed to the tool, used for the `<type> · <priority>` line.
 * @example
 * buildTicketInfo('create_jira_ticket', { status: 'success', data: { ticketKey: 'LEAP-1' } });
 * // { key: 'LEAP-1', url: null }
 */
export function buildTicketInfo(
  toolName: string,
  output: unknown,
  input?: Record<string, unknown>,
): LeapBotTicket | null {
  if (toolName !== TICKET_TOOL_NAME) return null;
  const parsed = parseOutput(output);
  if (!isRecord(parsed) || parsed['status'] !== 'success') return null;
  const data = parsed['data'] ?? parsed['result'] ?? parsed;
  const key = firstString(data, ['ticketKey', 'key', 'issueKey']);
  if (!key) return null;
  const issueType = firstString(input, ['issueType']);
  const priority = firstString(input, ['priority']);
  return {
    key,
    url: toHttpsUrl(firstString(data, ['ticketUrl', 'url', 'self'])),
    summary: firstString(data, ['summary']) ?? firstString(input, ['summary']),
    details: [issueType, priority].filter((part): part is string => part !== undefined).join(' · ') || undefined,
    warning: firstString(data, ['warning']),
  };
}

/**
 * Sources of a successful knowledge-base search, at most three, with their URL kept only when it
 * is `https://`. Empty for any other tool, for `no_results` and for errors.
 *
 * @param toolName - Tool that ended.
 * @param output - Raw output of the tool.
 */
export function extractSources(toolName: string, output: unknown): LeapBotSource[] {
  if (toolName !== KNOWLEDGE_TOOL_NAME) return [];
  const parsed = parseOutput(output);
  if (!isRecord(parsed) || parsed['status'] !== 'success') return [];
  const structured = parsed['structured_data'];
  const sources = isRecord(structured) ? structured['sources'] : undefined;
  if (!Array.isArray(sources)) return [];
  const result: LeapBotSource[] = [];
  for (const source of sources) {
    const title = firstString(source, ['title']);
    if (!title) continue;
    result.push({ title, url: toHttpsUrl(firstString(source, ['url'])) });
    if (result.length === MAX_SOURCES) break;
  }
  return result;
}

/**
 * Text of the step shown in the conversation while a tool runs.
 *
 * @param toolName - Tool LeapBot started.
 */
export function toolStepLabel(toolName: string): string {
  if (toolName === KNOWLEDGE_TOOL_NAME) return 'Buscando en la base de conocimiento…';
  if (toolName === TICKET_TOOL_NAME) return 'Creando el ticket en Jira…';
  return `Usando herramienta: ${toolName}`;
}

/** Text of the step shown above an answer that used the knowledge base. */
export function sourcesStepLabel(count: number): string {
  return `Consultó la base de conocimiento · ${count} ${count === 1 ? 'fuente' : 'fuentes'}`;
}

/**
 * Builds the action card for an `ActionRequired` event. The URL is kept only when it is
 * `https://`; Atlassian authorization gets the Spanish Jira copy.
 *
 * @param data - Payload of the event, read defensively.
 */
export function buildActionCard(data: LeapBotActionData): LeapBotActionCard {
  const rawUrl = data.actionUrl ?? data.authUrl ?? data.url;
  const url = toHttpsUrl(rawUrl);
  const isJiraAuth =
    (url?.startsWith('https://auth.atlassian.com') ?? false) ||
    data.provider === 'jira' ||
    /jira/i.test(data.message ?? '');
  if (isJiraAuth) {
    return {
      title: 'Autorización de Jira requerida',
      message:
        'LeapBot necesita tu permiso para acceder a Jira y crear el ticket. Haz clic en el botón para autorizar.',
      actionLabel: 'Autorizar en Jira',
      actionUrl: url,
      isJiraAuth: true,
    };
  }
  return {
    title: data.title ?? 'Acción requerida',
    message: data.message ?? '',
    actionLabel: data.actionLabel ?? 'Continuar',
    actionUrl: url,
    isJiraAuth: false,
  };
}
